import type { ReactNode } from "react";
import React from "react";

/**
 * Alert component that is used to display notifications.
 *
 * This should be used to display messages to the user that are important and need to be seen.
 * It uses role="alert" to ensure that screen readers will announce the message to the user immediately.
 *
 * This component should not be used for general information or tips. It should also not be used for inline validation
 * messages that are not critical to the user's experience.
 *
 * @param {Object} props - The properties that define the Alert component.
 * @param {('info' | 'danger' | 'success' | 'warning')} props.type - The type of alert to display. Options are: info, danger, success, warning. Default is 'info'.
 * @param {ReactNode} props.children - The children of the component. This is usually the message that you want to display in the alert.
 * @param {boolean} props.fullWidth - If true, the alert will take the full width of its container. Default is false.
 * @returns {ReactElement} The React Element created by the Alert component.
 */
const Alert: React.FC<{
  type?: "info" | "danger" | "success" | "warning";
  children: ReactNode;
  fullWidth?: boolean;
}> = ({ type = "info", children, fullWidth = false }) => {
  let baseClass = "p-4 mb-4 text-sm rounded-lg";

  switch (type) {
    case "info":
      baseClass += " text-blue-800 bg-blue-50";
      break;
    case "danger":
      baseClass += " text-red-800 bg-red-50";
      break;
    case "success":
      baseClass += " text-green-800 bg-green-50";
      break;
    case "warning":
      baseClass += " text-yellow-800 bg-yellow-50";
      break;
  }

  if (fullWidth) {
    baseClass += " w-full";
  }

  return (
    <div className={baseClass} role="alert">
      {children}
    </div>
  );
};

export default Alert;
